import { CheckoutController } from "./controller";
import { applyModalStyles } from "./modal-styles";
import type { CheckoutOptions, CompleteEvent, ErrorEvent } from "./types";

/**
 * Open a modal with the Borga checkout iframe inside. Returns a handle the
 * caller can use to close it programmatically.
 */
export interface CheckoutHandle {
  close: () => void;
}

/**
 * Hard backstop for the loading state. If neither `borga:ready` nor the
 * iframe's own `load` event ever arrives — a blocked frame, a dead network, a
 * checkout deploy that 500s — we reveal the iframe anyway rather than leave the
 * payer staring at a spinner forever. 8s is comfortably longer than a cold
 * hosted-page load on a slow connection but short enough that a broken load
 * still surfaces the page's own error UI.
 */
const READY_TIMEOUT_MS = 8000;

/**
 * Grace period after the iframe's `load` event. The hosted page fetches the
 * session *after* load and posts `borga:ready` when it starts painting real
 * content, so waiting a beat avoids flashing its skeleton. An older checkout
 * deployment never sends `borga:ready` at all — for those we reveal when this
 * elapses, which is still no later than today's behaviour by more than ~1s.
 */
const READY_GRACE_AFTER_LOAD_MS = 1200;

/**
 * Floor for a `borga:resize` height — never collapse the panel to a sliver on
 * the hosted page's say-so. The CSS still shrinks the frame below this when
 * the visible viewport itself is smaller (a phone with the keyboard up).
 */
const MIN_PANEL_HEIGHT = 320;

/**
 * Open the hosted checkout in a modal overlay on the merchant's page. The
 * overlay lives in a closed shadow root so merchant CSS cannot restyle it
 * and no inline `<style>` is injected into the document. Resolves through
 * the callbacks in `options` exactly once; the returned handle's `close()`
 * counts as a cancel. Throws outside a browser.
 */
export function openCheckoutModal(options: CheckoutOptions): CheckoutHandle {
  if (typeof document === "undefined") {
    throw new Error(
      "borga.checkout.open() is only available in the browser.",
    );
  }

  let closed = false;
  let revealed = false;
  let controller: CheckoutController | null = null;
  let readyTimer: ReturnType<typeof setTimeout> | undefined;
  let graceTimer: ReturnType<typeof setTimeout> | undefined;
  // Last content height reported by the hosted page, unclamped. 0 = never
  // reported, in which case the panel keeps its CSS height.
  let contentHeight = 0;

  // The element we put in the merchant's page is bare: no id, no classes, no
  // styles. Everything visual lives in a *closed* shadow root, so merchant CSS
  // can't reach in and we never inject an inline <style> into their document
  // (which a strict `style-src` CSP would block outright).
  const hostEl = document.createElement("div");
  const shadow = hostEl.attachShadow({ mode: "closed" });
  applyModalStyles(shadow);
  // Baseline positioning set through CSSOM (not a `style` attribute, and not
  // subject to `style-src`), so the overlay still covers the page even in the
  // worst case: an engine without constructable stylesheets *and* a CSP that
  // blocks the <style> fallback. Duplicates the `:host` rule; identical values.
  hostEl.style.position = "fixed";
  hostEl.style.inset = "0";
  hostEl.style.zIndex = "2147483000";

  // On phones the on-screen keyboard doesn't shrink the layout viewport that
  // `position: fixed; inset: 0` covers — it shrinks the *visual* viewport, and
  // the browser then pans that around the page to reveal the focused field.
  // To a payer that looks like the modal sliding away under the keyboard and
  // the merchant's page scrolling instead of the form. So the host follows
  // the visual viewport (top/left/width/height) and the CSS sizes the panel
  // to the host: the modal shrinks to the area above the keyboard and the
  // hosted page scrolls inside the iframe. Browsers without the API keep the
  // `inset: 0` baseline.
  const visualViewport = window.visualViewport ?? null;
  let viewportFrame: number | null = null;
  const syncToVisualViewport = () => {
    viewportFrame = null;
    if (!visualViewport || closed) return;
    const { offsetTop, offsetLeft, width, height } = visualViewport;
    if (!(width > 0 && height > 0)) return;
    hostEl.style.top = `${offsetTop}px`;
    hostEl.style.left = `${offsetLeft}px`;
    hostEl.style.width = `${width}px`;
    hostEl.style.height = `${height}px`;
    hostEl.style.right = "auto";
    hostEl.style.bottom = "auto";
  };
  // The keyboard animates in over several resize/scroll events; one layout
  // per animation frame is plenty.
  const onVisualViewportChange = () => {
    if (viewportFrame === null) {
      viewportFrame = window.requestAnimationFrame(syncToVisualViewport);
    }
  };

  const overlay = document.createElement("div");
  overlay.className = "overlay";

  const panel = document.createElement("div");
  panel.className = "panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "true");
  panel.setAttribute("aria-label", "Payment");

  const closeBtn = document.createElement("button");
  closeBtn.className = "close";
  closeBtn.type = "button";
  closeBtn.setAttribute("aria-label", "Close");
  // SVG, not "×": the glyph's vertical position depends on the font.
  const SVG_NS = "http://www.w3.org/2000/svg";
  const closeIcon = document.createElementNS(SVG_NS, "svg");
  closeIcon.setAttribute("viewBox", "0 0 16 16");
  closeIcon.setAttribute("width", "14");
  closeIcon.setAttribute("height", "14");
  closeIcon.setAttribute("aria-hidden", "true");
  closeIcon.setAttribute("focusable", "false");
  const closePath = document.createElementNS(SVG_NS, "path");
  closePath.setAttribute("d", "M3.5 3.5l9 9M12.5 3.5l-9 9");
  closePath.setAttribute("stroke", "currentColor");
  closePath.setAttribute("stroke-width", "1.75");
  closePath.setAttribute("stroke-linecap", "round");
  closePath.setAttribute("fill", "none");
  closeIcon.appendChild(closePath);
  closeBtn.appendChild(closeIcon);

  // Shown from the moment the modal opens, hidden on `borga:ready` (or by the
  // fallbacks below) so the payer never sees an empty white panel.
  const loading = document.createElement("div");
  loading.className = "loading";
  loading.setAttribute("role", "status");
  loading.setAttribute("aria-label", "Loading");
  const spinner = document.createElement("div");
  spinner.className = "spinner";
  spinner.setAttribute("aria-hidden", "true");
  loading.appendChild(spinner);

  // The iframe goes in hostEl's light DOM and is slotted in here. Chrome
  // autofill ignores iframes inside shadow roots.
  const host = document.createElement("div");
  host.className = "frame";
  host.appendChild(document.createElement("slot"));

  panel.appendChild(closeBtn);
  panel.appendChild(loading);
  panel.appendChild(host);
  overlay.appendChild(panel);
  shadow.appendChild(overlay);

  const clearTimers = () => {
    if (readyTimer !== undefined) clearTimeout(readyTimer);
    if (graceTimer !== undefined) clearTimeout(graceTimer);
    readyTimer = undefined;
    graceTimer = undefined;
  };

  const cleanup = () => {
    if (closed) return;
    closed = true;
    clearTimers();
    try {
      controller?.destroy();
    } catch {
      /* ignore */
    }
    document.removeEventListener("keydown", onKey);
    if (visualViewport) {
      visualViewport.removeEventListener("resize", onVisualViewportChange);
      visualViewport.removeEventListener("scroll", onVisualViewportChange);
    }
    if (viewportFrame !== null) window.cancelAnimationFrame(viewportFrame);
    if (hostEl.parentNode) hostEl.parentNode.removeChild(hostEl);
    document.body.style.overflow = prevOverflow;
  };

  const dismissAsCancel = () => {
    if (closed) return;
    cleanup();
    options.onCancel?.();
  };

  const onComplete = (r: CompleteEvent) => {
    cleanup();
    options.onComplete?.(r);
  };

  const onError = (err: ErrorEvent) => {
    cleanup();
    options.onError?.(err);
  };

  /** Swap the loading state for the iframe. Idempotent. */
  const reveal = () => {
    if (revealed || closed) return;
    revealed = true;
    clearTimers();
    loading.classList.add("is-hidden");
    host.classList.add("is-ready");
  };

  /**
   * Apply the last reported content height. Only the floor is enforced here;
   * the stylesheet caps the frame at whatever fits in the (visual) viewport,
   * so a frame taller than the screen scrolls inside the iframe instead.
   */
  const applyContentHeight = () => {
    if (!contentHeight) return;
    const next = Math.max(contentHeight, MIN_PANEL_HEIGHT);
    host.style.height = `${Math.round(next)}px`;
  };

  closeBtn.addEventListener("click", dismissAsCancel);
  overlay.addEventListener("click", (e) => {
    // backdrop click (not panel click)
    if (e.target === overlay) dismissAsCancel();
  });
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") dismissAsCancel();
  };
  document.addEventListener("keydown", onKey);
  if (visualViewport) {
    visualViewport.addEventListener("resize", onVisualViewportChange);
    visualViewport.addEventListener("scroll", onVisualViewportChange);
    syncToVisualViewport();
  }

  // Lock scroll
  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";

  document.body.appendChild(hostEl);

  readyTimer = setTimeout(reveal, READY_TIMEOUT_MS);

  controller = new CheckoutController({
    sessionId: options.sessionId,
    clientSecret: options.clientSecret,
    checkoutUrl: options.checkoutUrl,
    onComplete,
    onCancel: () => dismissAsCancel(),
    onError,
    onReady: reveal,
    onFrameLoad: () => {
      if (revealed || closed) return;
      graceTimer = setTimeout(reveal, READY_GRACE_AFTER_LOAD_MS);
    },
    onResize: (height) => {
      contentHeight = height;
      applyContentHeight();
    },
  });
  controller.mount(hostEl);

  return {
    close: dismissAsCancel,
  };
}
